import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, ClipboardCheck, Clock3, RefreshCw, ShieldAlert } from 'lucide-react';
import {
  PUBLIC_CLIENT_STATUSES,
  publicClientStatusAdminApi,
  type AdminPublicClientStatusDto,
  type ClientStatusSearchResult,
  type PublicClientStatus,
  type PublicClientStatusAuditEntryDto,
  type ReverificationDto,
  type ReverificationQueueItemDto,
} from '../api/publicClientStatus';
import { formatPublicIdDisplay } from '../utils/formatPublicId';

const STATUS_LABELS: Record<PublicClientStatus, string> = {
  UNDER_REVIEW: 'Application under review',
  ACTION_REQUIRED: 'Action required',
  OPENING_ACCOUNTS: 'Opening accounts',
  ACTIVE: 'Active',
  DECLINED: 'Declined',
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function statusTone(status: PublicClientStatus): string {
  if (status === 'ACTIVE') return 'bg-emerald-100 text-emerald-900';
  if (status === 'DECLINED') return 'bg-red-100 text-red-900';
  if (status === 'ACTION_REQUIRED') return 'bg-amber-100 text-amber-950';
  if (status === 'OPENING_ACCOUNTS') return 'bg-violet-100 text-violet-900';
  return 'bg-sky-100 text-sky-900';
}

function statusLabel(status: string): string {
  return STATUS_LABELS[status as PublicClientStatus] ?? status.replaceAll('_', ' ');
}

function StatusBadge({ status }: { status: PublicClientStatus }) {
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone(status)}`}>{STATUS_LABELS[status]}</span>;
}

function ClientStatusManagementPage() {
  const [queue, setQueue] = useState<ReverificationQueueItemDto[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState<ClientStatusSearchResult[]>([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [clientSearchError, setClientSearchError] = useState('');
  const [selected, setSelected] = useState<AdminPublicClientStatusDto | null>(null);
  const [audit, setAudit] = useState<PublicClientStatusAuditEntryDto[]>([]);
  const [activeReverification, setActiveReverification] = useState<ReverificationDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [publishStatus, setPublishStatus] = useState<PublicClientStatus>('UNDER_REVIEW');
  const [reasonCode, setReasonCode] = useState('');
  const [reason, setReason] = useState('');
  const [requiredDocumentType, setRequiredDocumentType] = useState('');

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    setQueueError('');
    try {
      setQueue(await publicClientStatusAdminApi.getReverificationQueue());
    } catch (error: unknown) {
      setQueueError(error instanceof Error ? error.message : 'Unable to load the reverification queue.');
    } finally {
      setQueueLoading(false);
    }
  }, []);

  const loadClient = useCallback(async (clientId: string) => {
    const normalizedId = clientId.trim();
    if (!normalizedId) return;
    setDetailLoading(true);
    setDetailError('');
    setNotice('');
    try {
      const [current, history, reverifications] = await Promise.all([
        publicClientStatusAdminApi.getStatus(normalizedId),
        publicClientStatusAdminApi.getAudit(normalizedId),
        publicClientStatusAdminApi.getReverificationHistory(normalizedId),
      ]);
      const openReverification = reverifications.find((item) => !['RESOLVED', 'CANCELLED'].includes(item.status)) ?? null;
      const next = current ?? {
        clientId: normalizedId,
        status: 'UNDER_REVIEW' as const,
        version: 0,
      };
      next.reverificationId = openReverification?.id ?? null;
      next.reverificationState = openReverification?.status ?? null;
      next.reasonCode = openReverification?.reasonCode ?? next.reasonCode;
      next.reason = openReverification?.reasonComment ?? next.reason;
      setSelected(next);
      setActiveReverification(openReverification);
      setAudit(history.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      setPublishStatus(next.status);
      setReasonCode(next.reasonCode ?? '');
      setReason(next.reason ?? '');
    } catch (error: unknown) {
      setSelected(null);
      setAudit([]);
      setDetailError(error instanceof Error ? error.message : 'Unable to load the client status.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const findClients = async () => {
    const query = clientSearchQuery.trim();
    if (!query) return;
    setClientSearchLoading(true);
    setClientSearchError('');
    try {
      setClientSearchResults(await publicClientStatusAdminApi.searchClients(query));
    } catch (error: unknown) {
      setClientSearchResults([]);
      setClientSearchError(error instanceof Error ? error.message : 'Unable to search clients.');
    } finally {
      setClientSearchLoading(false);
    }
  };

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const runReverificationAction = async (action: () => Promise<unknown>, successMessage: string) => {
    setBusy(true);
    setDetailError('');
    setNotice('');
    try {
      await action();
      setNotice(successMessage);
      if (selected) await Promise.all([loadClient(selected.clientId), loadQueue()]);
    } catch (error: unknown) {
      setDetailError(error instanceof Error ? error.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!selected) return;
    const trimmedReasonCode = reasonCode.trim();
    if (!trimmedReasonCode) {
      setDetailError('A reason code is required when publishing a client status.');
      return;
    }

    setBusy(true);
    setDetailError('');
    setNotice('');
    try {
      await publicClientStatusAdminApi.publish(selected.clientId, {
        status: publishStatus,
        reasonCode: trimmedReasonCode,
        reason: reason.trim() || null,
        expectedVersion: selected.version,
      });
      setNotice('Public status published.');
      await Promise.all([loadClient(selected.clientId), loadQueue()]);
    } catch (error: unknown) {
      setDetailError(error instanceof Error ? error.message : 'Unable to publish the public status.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Client status & reverification</h1>
          <p className="mt-1 text-sm text-gray-600">Publish client-facing status updates and complete reverification work.</p>
        </div>
        <button
          type="button"
          onClick={() => void Promise.all([loadQueue(), selected ? loadClient(selected.clientId) : Promise.resolve()])}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </header>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <label htmlFor="public-status-client-search" className="block text-sm font-semibold text-gray-900">
          Find client
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          <input
            id="public-status-client-search"
            value={clientSearchQuery}
            onChange={(event) => setClientSearchQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void findClients();
              }
            }}
            placeholder="Client name, email or public ID"
            className="min-w-[18rem] flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <button
            type="button"
            disabled={clientSearchLoading || !clientSearchQuery.trim()}
            onClick={() => void findClients()}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Find
          </button>
        </div>
        {clientSearchError ? <p className="mt-3 text-sm text-red-700">{clientSearchError}</p> : null}
        {clientSearchResults.length > 0 ? (
          <div className="mt-3 overflow-hidden rounded-lg border border-gray-200">
            {clientSearchResults.map((client) => (
              <button
                key={client.id}
                type="button"
                onClick={() => {
                  setClientSearchResults([]);
                  void loadClient(client.id);
                }}
                className="flex w-full items-center justify-between gap-4 border-b border-gray-100 px-4 py-3 text-left text-sm last:border-b-0 hover:bg-gray-50"
              >
                <span className="font-medium text-gray-900">
                  {client.companyName || [client.firstName, client.lastName].filter(Boolean).join(' ') || client.email || 'Unnamed client'}
                </span>
                <span className="font-mono text-xs text-gray-500">{formatPublicIdDisplay(client.publicId ?? undefined) || client.email}</span>
              </button>
            ))}
          </div>
        ) : null}
      </section>

      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-indigo-600" />
            <h2 className="font-semibold text-gray-900">Reverification queue</h2>
          </div>
          <span className="text-sm text-gray-500">{queue.length} item{queue.length === 1 ? '' : 's'}</span>
        </div>
        {queueLoading ? (
          <div className="flex items-center gap-2 px-5 py-10 text-sm text-gray-500">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading queue…
          </div>
        ) : queueError ? (
          <div className="px-5 py-5 text-sm text-red-700">{queueError}</div>
        ) : queue.length === 0 ? (
          <div className="px-5 py-10 text-sm text-gray-500">No clients currently require reverification.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-5 py-3">Client</th>
                  <th className="px-5 py-3">Public status</th>
                  <th className="px-5 py-3">Reverification</th>
                  <th className="px-5 py-3">Due</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {queue.map((item) => (
                  <tr key={item.reverificationId ?? item.clientId} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-900">{item.clientName || 'Unnamed client'}</p>
                      <p className="mt-0.5 font-mono text-xs text-gray-500">
                        {formatPublicIdDisplay(item.clientPublicId ?? undefined) || item.clientId}
                      </p>
                    </td>
                    <td className="px-5 py-3"><StatusBadge status={item.status} /></td>
                    <td className="px-5 py-3 text-gray-700">{item.reverificationState || 'Queued'}</td>
                    <td className="px-5 py-3 text-gray-600">{formatDate(item.reverificationDueAt)}</td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => void loadClient(item.clientId)}
                        className="font-semibold text-indigo-700 hover:text-indigo-900"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {detailLoading ? (
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-8 text-sm text-gray-500">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Loading client status…
        </div>
      ) : null}

      {detailError ? (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-900">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <span>{detailError}</span>
        </div>
      ) : null}

      {notice ? (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900">
          <CheckCircle2 className="h-5 w-5" />
          <span>{notice}</span>
        </div>
      ) : null}

      {selected ? (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,0.8fr)]">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Client-facing status</p>
                <h2 className="mt-1 text-lg font-semibold text-gray-900">{selected.clientName || 'Client status'}</h2>
                <p className="mt-1 font-mono text-xs text-gray-500">
                  {formatPublicIdDisplay(selected.clientPublicId ?? undefined) || selected.clientId}
                </p>
              </div>
              <StatusBadge status={selected.status} />
            </div>
            <dl className="mt-5 grid gap-4 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Version</dt>
                <dd className="mt-1 font-semibold text-gray-900">{selected.version}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Published</dt>
                <dd className="mt-1 text-gray-900">{formatDate(selected.publishedAt)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Reason code</dt>
                <dd className="mt-1 font-medium text-gray-900">{selected.reasonCode || '—'}</dd>
              </div>
            </dl>

            <div className="mt-6 border-t border-gray-100 pt-5">
              <h3 className="font-semibold text-gray-900">Publish status</h3>
              <p className="mt-1 text-sm text-gray-600">Publishing uses the displayed version to prevent overwriting a newer decision.</p>
              <div className="mt-4 grid gap-4">
                <label className="text-sm font-medium text-gray-700">
                  Public status
                  <select
                    value={publishStatus}
                    onChange={(event) => setPublishStatus(event.target.value as PublicClientStatus)}
                    disabled={busy}
                    className="mt-1.5 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {PUBLIC_CLIENT_STATUSES.map((status) => <option key={status} value={status}>{STATUS_LABELS[status]}</option>)}
                  </select>
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Reason code
                  <input
                    value={reasonCode}
                    onChange={(event) => setReasonCode(event.target.value)}
                    disabled={busy}
                    maxLength={100}
                    className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Client-facing reason
                  <textarea
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    disabled={busy}
                    rows={4}
                    maxLength={2000}
                    className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>
              </div>
              <button
                type="button"
                disabled={busy || !reasonCode.trim()}
                onClick={() => void publish()}
                className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                Publish version {selected.version + 1}
              </button>
            </div>
          </section>

          <div className="space-y-6">
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-amber-600" />
                <h2 className="font-semibold text-gray-900">Reverification actions</h2>
              </div>
              <dl className="mt-4 grid gap-3 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">State</dt>
                  <dd className="font-medium text-gray-900">{selected.reverificationState || 'Not required'}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Started</dt>
                  <dd className="text-right text-gray-900">{formatDate(selected.reverificationStartedAt)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Due</dt>
                  <dd className="text-right text-gray-900">{formatDate(selected.reverificationDueAt)}</dd>
                </div>
              </dl>
              <label className="mt-5 block text-sm font-medium text-gray-700">
                Required document type
                <input
                  value={requiredDocumentType}
                  onChange={(event) => setRequiredDocumentType(event.target.value)}
                  disabled={busy}
                  maxLength={100}
                  placeholder="Proof of address"
                  className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </label>
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (!reasonCode.trim()) {
                      setDetailError('A reason code is required to start reverification.');
                      return;
                    }
                    void runReverificationAction(
                      () => publicClientStatusAdminApi.startReverification(selected.clientId, reasonCode.trim(), reason.trim() || null),
                      'Reverification started.',
                    );
                  }}
                  className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  Start
                </button>
                <button
                  type="button"
                  disabled={busy || !activeReverification}
                  onClick={() => {
                    if (!activeReverification || !requiredDocumentType.trim()) {
                      setDetailError('Select an active reverification and enter a required document type.');
                      return;
                    }
                    void runReverificationAction(
                      () => publicClientStatusAdminApi.requestReverificationInformation(
                        activeReverification.id,
                        requiredDocumentType.trim(),
                        reason.trim() || null,
                      ),
                      'Information request sent.',
                    );
                  }}
                  className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-950 hover:bg-amber-100 disabled:opacity-50"
                >
                  Request information
                </button>
                <button
                  type="button"
                  disabled={busy || !activeReverification}
                  onClick={() => {
                    if (!activeReverification) return;
                    void runReverificationAction(
                      () => publicClientStatusAdminApi.completeReverification(activeReverification.id, reason.trim() || null),
                      'Reverification completed.',
                    );
                  }}
                  className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-950 hover:bg-emerald-100 disabled:opacity-50"
                >
                  Complete
                </button>
              </div>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <Clock3 className="h-5 w-5 text-indigo-600" />
                <h2 className="font-semibold text-gray-900">Compliance audit</h2>
              </div>
              {audit.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">No status or reverification actions recorded yet.</p>
              ) : (
                <ol className="mt-4 space-y-4">
                  {audit.map((entry) => (
                    <li key={entry.id} className="border-l-2 border-indigo-200 pl-3">
                      <p className="text-sm font-semibold text-gray-900">{entry.action}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {formatDate(entry.createdAt)}
                        {entry.version != null ? ` · version ${entry.version}` : ''}
                      </p>
                      {entry.fromStatus || entry.toStatus ? (
                        <p className="mt-1 text-xs text-gray-700">
                          {entry.fromStatus ? statusLabel(entry.fromStatus) : 'No previous status'} →{' '}
                          {entry.toStatus ? statusLabel(entry.toStatus) : 'No new status'}
                        </p>
                      ) : null}
                      {entry.reasonCode ? <p className="mt-1 text-xs font-medium text-gray-700">{entry.reasonCode}</p> : null}
                      {entry.reason ? <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{entry.reason}</p> : null}
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ClientStatusManagementPage;
